import {
  AuditActorType,
  AuditEntryType,
  type CaseRecordAuditEntry,
} from '@compass/client/types/case_record';
import type { User } from '@compass/client/types/user';
import { cn } from '@utils/index';
import { ChevronDown, ChevronUp, Cog } from 'lucide-react';
import React, { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { UserAvatar } from '@/components/UserAvatar';
import { useStore } from '@/store';
import type { FormField } from '@/types/formSchema';
import { getRelativeTime } from '@/utils/date';

import { CaseHistoryDiff } from './CaseHistoryDiff';

interface CaseHistoryEntryProps {
  entry: CaseRecordAuditEntry;
  user?: User;
  className?: string;
  /** Map of field IDs to their full schema definitions from the workflow UI schema */
  fieldSchemaMap?: Record<string, FormField>;
  /** Previous values for this entry's changed fields (computed from history reconstruction) */
  previousValues?: Record<string, any>;
}

/**
 * Get the display name for an actor.
 */
function getActorName(entry: CaseRecordAuditEntry, user?: User): string {
  if (entry.actor?.actor_type === AuditActorType.AUDIT_ACTOR_TYPE_SERVICE) {
    return entry.actor.service?.service_name || 'System';
  }

  if (user) {
    return user.name || user.email || 'Unknown';
  }

  return 'Unknown';
}

/**
 * Get badge variant and label based on operation type.
 */
function getOperationBadge(operation: string | undefined): { label: string; variant: string } {
  switch (operation?.toLowerCase()) {
    case 'insert':
      return { label: 'CREATED', variant: 'bg-green-100 text-green-800 border-green-200' };
    case 'update':
      return { label: 'UPDATED', variant: 'bg-blue-100 text-blue-800 border-blue-200' };
    case 'replace':
      return { label: 'REPLACED', variant: 'bg-orange-100 text-orange-800 border-orange-200' };
    case 'delete':
      return { label: 'DELETED', variant: 'bg-red-100 text-red-800 border-red-200' };
    default:
      return { label: 'MODIFIED', variant: 'bg-muted text-muted-foreground border-border' };
  }
}

/**
 * Check if the entry has diff content to show.
 */
function hasDiffContent(entry: CaseRecordAuditEntry): boolean {
  if (entry.entry_type === AuditEntryType.AUDIT_ENTRY_TYPE_FULL) {
    return false;
  }

  const desc = entry.update_description;
  if (!desc) return false;

  const hasUpdated = !!(desc.updated_fields && Object.keys(desc.updated_fields).length > 0);
  const hasRemoved = !!(desc.removed_fields && desc.removed_fields.length > 0);

  return hasUpdated || hasRemoved;
}

/**
 * Component to display a single case history audit entry.
 * Follows the timeline pattern from NotesPanel.
 */
export const CaseHistoryEntry: React.FC<CaseHistoryEntryProps> = React.memo(
  ({ entry, user, className, fieldSchemaMap, previousValues }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { getTimeZone } = useStore();
    const timeZone = getTimeZone();

    const isServiceActor = entry.actor?.actor_type === AuditActorType.AUDIT_ACTOR_TYPE_SERVICE;
    const actorName = getActorName(entry, user);
    const { label: badgeLabel, variant: badgeVariant } = getOperationBadge(entry.operation);
    const isCreated = entry.operation?.toLowerCase() === 'insert';
    const showDiff = !isCreated && hasDiffContent(entry);
    const isFullSnapshot = !isCreated && entry.entry_type === AuditEntryType.AUDIT_ENTRY_TYPE_FULL;

    return (
      <div className={cn('relative flex items-start gap-3', className)}>
        {/* Avatar - sits ON the timeline line */}
        <div className="relative z-10 shrink-0">
          {isServiceActor ? (
            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center border border-border">
              <Cog className="h-4 w-4 text-muted-foreground" />
            </div>
          ) : (
            <UserAvatar name={actorName} userId={user?.id} size="lg" />
          )}
        </div>

        {/* Content area */}
        <div className="flex-1 min-w-0 pt-0.5">
          {/* Top row: Name + badge stacked on left, Timestamp on right */}
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">{actorName}</p>
              <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 mt-1', badgeVariant)}>
                {badgeLabel}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground shrink-0">
              {getRelativeTime(entry.timestamp, timeZone)}
            </p>
          </div>

          {/* Expandable diff section */}
          {showDiff && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {isExpanded ? 'Hide changes' : 'Show changes'}
                {isExpanded ? (
                  <ChevronUp className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3" />
                )}
              </button>

              {isExpanded && (
                <div className="mt-2">
                  <CaseHistoryDiff
                    updateDescription={entry.update_description}
                    fieldSchemaMap={fieldSchemaMap}
                    previousValues={previousValues}
                  />
                </div>
              )}
            </div>
          )}

          {/* Full snapshot - expandable view */}
          {isFullSnapshot && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {isExpanded ? 'Hide details' : 'View details'}
                {isExpanded ? (
                  <ChevronUp className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3" />
                )}
              </button>

              {isExpanded && entry.case_record && (
                <div className="mt-2 p-3 rounded-lg border border-border space-y-2 text-xs">
                  {entry.case_record.state && (
                    <div className="flex gap-2">
                      <span className="text-muted-foreground font-medium">State:</span>
                      <span className="text-foreground">{entry.case_record.state}</span>
                    </div>
                  )}
                  {entry.case_record.assigned && (
                    <div className="flex gap-2">
                      <span className="text-muted-foreground font-medium">Assigned:</span>
                      <span className="text-foreground">{entry.case_record.assigned}</span>
                    </div>
                  )}
                  {entry.case_record.reference_id && (
                    <div className="flex gap-2">
                      <span className="text-muted-foreground font-medium">Reference:</span>
                      <span className="text-foreground font-mono text-[10px]">
                        {entry.case_record.reference_id}
                      </span>
                    </div>
                  )}
                  <p className="text-muted-foreground italic pt-1 border-t border-border/50">
                    Full record snapshot captured at this point
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  },
);

CaseHistoryEntry.displayName = 'CaseHistoryEntry';
